'use client'

import React from 'react'
import { ArrowRight, ArrowDown, CheckCircle, XCircle } from 'lucide-react'

/**
 * Reusable Blog Diagrams
 *
 * Generic, prop-driven infographic components so any post can drop in a
 * visual without building a bespoke component. Covers the five recurring
 * shapes: magnitude comparison, process/trajectory, composition/share,
 * before-after contrast, and headline-metric grid.
 *
 * Theming follows the blog diagram convention (data-theme dark, NOT Tailwind
 * dark:): bg-muted/50 shell, bg-background cards, border-border/60, inline
 * ${color}15 tints. Brand colors: #008dcc, #FDC304, #F90B8A, #8b5cf6, #3CC29E.
 */

const BRAND_CYCLE = ['#008dcc', '#F90B8A', '#FDC304', '#8b5cf6', '#3CC29E']
const colorAt = (i: number, c?: string) =>
  c ?? BRAND_CYCLE[i % BRAND_CYCLE.length]

function DiagramShell({
  title,
  subtitle,
  children,
}: {
  title?: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      {(title || subtitle) && (
        <div className='text-center mb-6'>
          {title && (
            <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
              {title}
            </h3>
          )}
          {subtitle && (
            <p className='text-sm text-muted-foreground'>{subtitle}</p>
          )}
        </div>
      )}
      {children}
    </div>
  )
}

/* 1. Magnitude comparison: horizontal bars. */
interface BarItem {
  label: string
  value: number
  display?: string
  color?: string
}
export function ComparisonBarsDiagram({
  title,
  subtitle,
  items,
  max,
  unit = '%',
}: {
  title?: string
  subtitle?: string
  items: BarItem[]
  max?: number
  unit?: string
}) {
  const top = max ?? Math.max(...items.map(i => i.value), 0)
  return (
    <DiagramShell title={title} subtitle={subtitle}>
      <div className='flex flex-col gap-4'>
        {items.map((item, i) => {
          const color = colorAt(i, item.color)
          const pct = top > 0 ? Math.round((item.value / top) * 100) : 0
          return (
            <div key={item.label}>
              <div className='flex items-center justify-between mb-1.5'>
                <span className='text-sm font-semibold text-foreground'>
                  {item.label}
                </span>
                {/* Value text stays on the foreground token: brand fills
                    (#008dcc 3.70:1, #F90B8A 3.86:1 on white) miss the 4.5:1
                    WCAG AA bar for 14px text in the light theme. The bar
                    below carries the color. */}
                <span className='text-sm font-bold text-foreground'>
                  {item.display ?? `${item.value}${unit}`}
                </span>
              </div>
              <div className='h-3 w-full rounded-full bg-muted overflow-hidden'>
                <div
                  className='h-full rounded-full'
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </DiagramShell>
  )
}

/* 2. Process / trajectory: steps with connectors. */
interface ProgressionStep {
  label: string
  value?: string
  sublabel?: string
  color?: string
}
export function ProgressionStepsDiagram({
  title,
  subtitle,
  steps,
}: {
  title?: string
  subtitle?: string
  steps: ProgressionStep[]
}) {
  return (
    <DiagramShell title={title} subtitle={subtitle}>
      {/* md:flex-wrap + min-w-0 on each step. Without min-w-0 a `flex-1` child
          cannot shrink below its min-content width, so a five-step ladder
          overflowed the shell's right border instead of fitting inside it.
          The wrap lets a long ladder fall onto a second row rather than
          escaping, and md:min-w keeps the columns wide enough to read
          instead of breaking to one word per line. */}
      <div className='flex flex-col md:flex-row md:flex-wrap md:items-stretch gap-2'>
        {steps.map((step, i) => {
          const color = colorAt(i, step.color)
          return (
            <React.Fragment key={step.label}>
              <div className='flex-1 min-w-0 md:min-w-[9rem] p-4 rounded-lg bg-background border border-border/60 text-center'>
                {step.value && (
                  <div
                    className='text-2xl font-bold mb-1 leading-none'
                    style={{ color }}
                  >
                    {step.value}
                  </div>
                )}
                <p className='text-sm font-semibold text-foreground leading-tight'>
                  {step.label}
                </p>
                {step.sublabel && (
                  <p className='text-xs text-muted-foreground mt-1'>
                    {step.sublabel}
                  </p>
                )}
              </div>
              {i < steps.length - 1 && (
                <div className='flex shrink-0 items-center justify-center text-muted-foreground'>
                  <ArrowRight className='hidden md:block w-5 h-5 rtl:-scale-x-100' />
                  <ArrowDown className='md:hidden w-4 h-4' />
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    </DiagramShell>
  )
}

/* 3. Composition / share: SVG donut with legend. */
interface ShareSegment {
  label: string
  value: number
  color?: string
}
export function ShareDonutDiagram({
  title,
  subtitle,
  segments,
  centerLabel,
  centerSublabel,
  unit = '%',
}: {
  title?: string
  subtitle?: string
  segments: ShareSegment[]
  centerLabel?: string
  centerSublabel?: string
  unit?: string
}) {
  const total = segments.reduce((s, seg) => s + seg.value, 0)
  const radius = 70
  const circ = 2 * Math.PI * radius
  let cumulative = 0
  return (
    <DiagramShell title={title} subtitle={subtitle}>
      <div className='flex flex-col md:flex-row items-center gap-8'>
        <div className='relative w-44 h-44 flex-shrink-0'>
          <svg viewBox='0 0 180 180' className='w-44 h-44 -rotate-90'>
            {segments.map((seg, i) => {
              const color = colorAt(i, seg.color)
              const len = total > 0 ? (seg.value / total) * circ : 0
              const node = (
                <circle
                  key={seg.label}
                  cx='90'
                  cy='90'
                  r={radius}
                  fill='none'
                  stroke={color}
                  strokeWidth='22'
                  strokeDasharray={`${len} ${circ - len}`}
                  strokeDashoffset={-cumulative}
                />
              )
              cumulative += len
              return node
            })}
          </svg>
          {centerLabel && (
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <span className='text-2xl font-bold text-foreground leading-none'>
                {centerLabel}
              </span>
              {centerSublabel && (
                <span className='text-xs text-muted-foreground mt-1'>
                  {centerSublabel}
                </span>
              )}
            </div>
          )}
        </div>
        <div className='flex-1 w-full flex flex-col gap-2'>
          {segments.map((seg, i) => {
            const color = colorAt(i, seg.color)
            const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0
            return (
              <div
                key={seg.label}
                className='flex items-center justify-between gap-3'
              >
                <span className='flex items-center gap-2 text-sm text-foreground'>
                  <span
                    className='inline-block w-3 h-3 rounded-sm flex-shrink-0'
                    style={{ backgroundColor: color }}
                  />
                  {seg.label}
                </span>
                <span className='text-sm font-bold' style={{ color }}>
                  {pct}
                  {unit}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </DiagramShell>
  )
}

/* 4. Contrast: before / after columns. */
interface BeforeAfterSide {
  label: string
  stat?: string
  points: string[]
}
export function BeforeAfterDiagram({
  title,
  subtitle,
  before,
  after,
}: {
  title?: string
  subtitle?: string
  before: BeforeAfterSide
  after: BeforeAfterSide
}) {
  return (
    <DiagramShell title={title} subtitle={subtitle}>
      <div className='grid md:grid-cols-2 gap-6'>
        <div className='p-5 rounded-lg bg-background border border-red-500/30'>
          <div className='text-center mb-4'>
            <span className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-500 text-xs font-semibold uppercase tracking-wider'>
              <XCircle className='w-3 h-3' />
              {before.label}
            </span>
          </div>
          {before.stat && (
            <div className='text-center text-2xl font-bold text-red-500 mb-3 leading-none'>
              {before.stat}
            </div>
          )}
          <ul className='flex flex-col gap-2'>
            {before.points.map(p => (
              <li
                key={p}
                className='flex items-start gap-2 text-sm text-foreground/80'
              >
                <XCircle className='w-4 h-4 text-red-500/70 flex-shrink-0 mt-0.5' />
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div className='p-5 rounded-lg bg-background border border-[#008dcc]/30'>
          <div className='text-center mb-4'>
            <span className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#008dcc]/10 border border-[#008dcc]/30 text-[#008dcc] text-xs font-semibold uppercase tracking-wider'>
              <CheckCircle className='w-3 h-3' />
              {after.label}
            </span>
          </div>
          {after.stat && (
            <div className='text-center text-2xl font-bold text-[#008dcc] mb-3 leading-none'>
              {after.stat}
            </div>
          )}
          <ul className='flex flex-col gap-2'>
            {after.points.map(p => (
              <li
                key={p}
                className='flex items-start gap-2 text-sm text-foreground/80'
              >
                <CheckCircle className='w-4 h-4 text-[#008dcc]/70 flex-shrink-0 mt-0.5' />
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </DiagramShell>
  )
}

/* 5. Headline metrics: grid of big-number cards. */
interface HighlightStat {
  value: string
  label: string
  color?: string
}
export function StatHighlightGrid({
  title,
  subtitle,
  stats,
}: {
  title?: string
  subtitle?: string
  stats: HighlightStat[]
}) {
  const cols =
    stats.length >= 4
      ? 'grid-cols-2 md:grid-cols-4'
      : stats.length === 3
        ? 'grid-cols-1 sm:grid-cols-3'
        : 'grid-cols-2'
  return (
    <DiagramShell title={title} subtitle={subtitle}>
      <div className={`grid gap-4 ${cols}`}>
        {stats.map((s, i) => {
          const color = colorAt(i, s.color)
          return (
            <div
              key={s.label}
              className='p-5 rounded-lg bg-background border border-border/60 text-center'
              style={{ borderTopColor: color, borderTopWidth: 3 }}
            >
              <div
                className='text-3xl font-bold leading-none'
                style={{ color }}
              >
                {s.value}
              </div>
              <p className='text-xs text-muted-foreground mt-2 leading-snug'>
                {s.label}
              </p>
            </div>
          )
        })}
      </div>
    </DiagramShell>
  )
}
